import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: `${API_URL}/iptv`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class IPTVService {
  // Get all channels
  async getChannels() {
    try {
      const response = await apiClient.get('/channels');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get active channels only
  async getActiveChannels() {
    try {
      const response = await apiClient.get('/channels/active');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get single channel
  async getChannel(id) {
    try {
      const response = await apiClient.get(`/channels/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new channel
  async createChannel(channelData) {
    try {
      const response = await apiClient.post('/channels', channelData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update channel
  async updateChannel(id, channelData) {
    try {
      const response = await apiClient.put(`/channels/${id}`, channelData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete channel
  async deleteChannel(id) {
    try {
      const response = await apiClient.delete(`/channels/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get categories
  async getCategories() {
    try {
      const response = await apiClient.get('/categories');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Reorder channels
  async reorderChannels(orders) {
    try {
      const response = await apiClient.post('/channels/reorder', { orders });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get M3U playlist URL
  getPlaylistUrl() {
    const token = localStorage.getItem('streamToken');
    return `${API_URL}/iptv/playlist.m3u?token=${token}`;
  }

  // Download M3U playlist
  async downloadPlaylist() {
    try {
      const response = await apiClient.get('/playlist.m3u', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'playlist.m3u');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get IPTV status
  async getStatus() {
    try {
      const response = await apiClient.get('/status');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors
  handleError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      const code = error.response.data?.code || 'UNKNOWN_ERROR';
      
      const customError = new Error(message);
      customError.code = code;
      customError.status = error.response.status;
      customError.data = error.response.data;
      
      return customError;
    } else if (error.request) {
      // Request made but no response
      const customError = new Error('No response from server');
      customError.code = 'NO_RESPONSE';
      return customError;
    } else {
      // Something else happened
      return error;
    }
  }
}

export default new IPTVService();
